<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="relative z-50" @close="close">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-900/70 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="relative w-full max-w-lg transform overflow-hidden rounded-2xl bg-white px-8 py-8 text-left shadow-2xl transition-all sm:my-8">
                            <button
                                type="button"
                                class="absolute top-4 right-4 text-gray-400 hover:text-gray-700"
                                @click="close"
                            >
                                <font-awesome-icon :icon="faXmark" class="h-5 w-5" />
                                <span class="sr-only">Close</span>
                            </button>

                            <DialogTitle as="h3" class="text-center text-2xl font-bold text-[#c19b1c]">
                                Switch to RiskGuard Pro
                            </DialogTitle>
                            <p class="mt-1 text-center text-sm text-[#c19b1c]">One plan for your whole account</p>

                            <div v-if="loading" class="mt-6 space-y-3" aria-busy="true">
                                <div class="h-4 w-2/3 animate-pulse rounded bg-gray-200" />
                                <div class="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
                                <div class="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                            </div>

                            <div v-else-if="needsCard" class="mt-6 text-center text-sm text-gray-700">
                                <p>Add a card first. It will be used for your RiskGuard Pro plan.</p>
                                <button
                                    v-if="stripeData"
                                    type="button"
                                    class="mt-4 rounded-md bg-rent-risk-blue-400 px-6 py-2 text-sm font-bold text-white hover:opacity-90"
                                    @click="showAddCard = true"
                                >
                                    Add Card
                                </button>
                                <a
                                    v-else
                                    :href="route('payment-methods.index')"
                                    class="mt-4 inline-block rounded-md bg-rent-risk-blue-400 px-6 py-2 text-sm font-bold text-white hover:opacity-90"
                                >
                                    Add a card on Billing
                                </a>
                            </div>

                            <div v-else-if="preview" class="mt-6 text-sm text-gray-800">
                                <p>
                                    Your new plan: <strong>RiskGuard Pro</strong> at
                                    <strong>{{ formatYearly(preview.tier.price) }}</strong> for your
                                    <strong>{{ preview.property_count }} {{ preview.property_count === 1 ? 'property' : 'properties' }}</strong>.
                                </p>
                                <p class="mt-1 text-xs text-gray-500">All your properties count toward the price, including ones on the free plan.</p>

                                <dl class="mt-5 divide-y divide-gray-100 rounded-lg border border-gray-200">
                                    <div class="flex justify-between px-4 py-2">
                                        <dt>RiskGuard Pro (1 year)</dt>
                                        <dd>{{ money(preview.tier_price_cents) }}</dd>
                                    </div>
                                    <div v-if="unusedTimeCredit > 0" class="flex justify-between px-4 py-2">
                                        <dt>Credit for unused time on your current plan</dt>
                                        <dd>−{{ money(unusedTimeCredit) }}</dd>
                                    </div>
                                    <div v-if="preview.existing_balance_cents < 0" class="flex justify-between px-4 py-2">
                                        <dt>Account credit</dt>
                                        <dd>−{{ money(-preview.existing_balance_cents) }}</dd>
                                    </div>
                                    <div v-if="preview.existing_balance_cents > 0" class="flex justify-between px-4 py-2">
                                        <dt>Balance owed</dt>
                                        <dd>{{ money(preview.existing_balance_cents) }}</dd>
                                    </div>
                                    <div class="flex justify-between px-4 py-2 font-bold">
                                        <dt>Charged today</dt>
                                        <dd>{{ money(preview.total_today_cents) }}</dd>
                                    </div>
                                    <div v-if="preview.credit_carried_cents > 0" class="flex justify-between px-4 py-2 text-green-700">
                                        <dt>Credit kept for your next bill</dt>
                                        <dd>{{ money(preview.credit_carried_cents) }}</dd>
                                    </div>
                                </dl>

                                <p class="mt-3 text-xs text-gray-500">
                                    Your current plan ends today and your rent payments / maintenance subscriptions are
                                    included in Pro. Your plan renews on {{ formatDate(preview.renewal_date) }}.
                                </p>
                            </div>

                            <p v-if="error" class="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{{ error }}</p>

                            <div class="mt-6 flex flex-col items-center gap-3">
                                <button
                                    v-if="preview && !needsCard"
                                    type="button"
                                    :disabled="switching"
                                    class="rounded-md bg-[#2f3a5e] px-8 py-3 text-base font-bold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                                    @click="confirmSwitch"
                                >
                                    {{ switching ? 'Switching…' : 'Switch to new plan' }}
                                </button>
                                <button type="button" class="text-sm text-gray-600 underline hover:text-gray-900" @click="close">
                                    Cancel
                                </button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>

    <AddCardPopup
        v-if="stripeData"
        :show="showAddCard"
        :stripe_data="stripeData"
        @card-added="handleCardAdded"
        @close="showAddCard = false"
    />
</template>

<script setup>
// "Switch to new plan" for a landlord on an earlier plan (86bcd9hg3, decisions 13–14):
// shows what switching costs today — the tier for ALL their properties, less credit for
// unused time — then switches through billing.pro.switch. A refused or declined switch
// changes nothing; the server's message is shown as is.
import { computed, ref, watch } from 'vue'
import axios from 'axios'
import { router } from '@inertiajs/vue3'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import AddCardPopup from '@/Shared/AddCardPopup.vue'
import { formatYearly } from '@/Shared/proPlanState'

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    // Stripe card data (FunctionHelpers::stripeDataForAddingCard) when the page has it,
    // so a landlord without a card can add one here; otherwise they are sent to Billing.
    stripeData: {
        type: Object,
        default: null,
    },
})

const emit = defineEmits(['close', 'switched'])

const loading = ref(false)
const switching = ref(false)
const preview = ref(null)
const needsCard = ref(false)
const showAddCard = ref(false)
const error = ref('')

const unusedTimeCredit = computed(() => (preview.value?.protect_credit_cents ?? 0) + (preview.value?.feature_credit_cents ?? 0))

watch(() => props.show, (open) => {
    if (open) {
        loadPreview()
    }
}, { immediate: true })

async function loadPreview() {
    loading.value = true
    error.value = ''
    needsCard.value = false

    try {
        const { data } = await axios.get(route('billing.pro.switch-preview'))
        preview.value = data.preview
    } catch (exception) {
        const response = exception.response?.data ?? {}
        preview.value = null
        needsCard.value = response.requires_card === true
        error.value = needsCard.value ? '' : (response.message || 'We could not load your price. Please try again.')
    } finally {
        loading.value = false
    }
}

async function confirmSwitch() {
    if (!preview.value) {
        return
    }

    switching.value = true
    error.value = ''

    try {
        await axios.post(route('billing.pro.switch'), {
            expected_tier_slug: preview.value.tier.slug,
            proration_date: preview.value.proration_date,
        })
        emit('switched')
        router.reload()
    } catch (exception) {
        const response = exception.response?.data ?? {}

        if (exception.response?.status === 409 && response.tier_changed) {
            // A property was added since the quote: show the new price before charging.
            await loadPreview()
            error.value = response.message
        } else {
            error.value = response.message || 'We could not switch your plan. Nothing was charged; please try again.'
        }
    } finally {
        switching.value = false
    }
}

function handleCardAdded() {
    showAddCard.value = false
    loadPreview()
}

function close() {
    if (!switching.value) {
        emit('close')
    }
}

function money(cents) {
    return `$${(Number(cents) / 100).toFixed(2)}`
}

function formatDate(isoDate) {
    if (!isoDate) {
        return ''
    }

    return new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}
</script>
